import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldCheck, Users, ChartLineUp, Scales, Briefcase, Target, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import InquiryModal from '../components/InquiryModal';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const About = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');
  const handleButtonClick = (context) => { setButtonContext(context); setIsModalOpen(true); };
  const [whyChooseUsRef] = useEmblaCarousel({ loop: true, align: 'start', slidesToScroll: 1, dragFree: false, skipSnaps: false, speed: 10 }, [Autoplay({ delay: 4000, stopOnInteraction: false, playOnInit: true })]);
  const [howWeWorkRef] = useEmblaCarousel({ loop: true, align: 'start' });

  return (
    <div className="min-h-screen pt-[72px]">

      {/* Hero Section */}
      <section data-testid="about-hero" className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2070" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-5xl text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-4">About Our Firm</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white tracking-tighter leading-[1.05] mb-6">
              Your Trusted Trademark Registration Partner
            </h1>
            <p className="text-base text-slate-300 leading-relaxed">
              We help businesses protect their brand identity through professional trademark registration, trademark filing services, and brand protection solutions. From startups to established companies, our team supports business owners looking to trademark a business name, trademark a logo, register a slogan, and secure valuable intellectual property assets.
            </p>
            <div className="mt-8">
              <button data-testid="about-protect-btn" onClick={() => handleButtonClick('About Hero - Protect My Brand')}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-white font-semibold text-sm px-8 py-5 rounded-md inline-flex items-center gap-2 transition-colors">
                Protect My Brand <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsJTIwZGlzY3Vzc2lvbnxlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Our team in action" 
                  className="w-full h-[480px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-[#044B3B] text-white p-8 rounded-lg shadow-xl hidden lg:block">
                <div className="text-4xl font-heading font-bold mb-1">15+</div>
                <div className="text-sm text-white/80">Years of<br/>Excellence</div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Our Story</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-6">
                Built on Brand Protection, Focused on Results
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Our mission is to help businesses confidently protect their brand identity through reliable trademark registration and brand protection services. Whether you want to trademark a business name, trademark a logo, register a slogan, or secure other valuable intellectual property assets, our streamlined process is designed to make trademark filing simple and efficient. We work with startups, entrepreneurs, and established companies seeking long term trademark protection, helping them strengthen their market presence, safeguard their brand value, and build a stronger foundation for future growth.
              </p>

              <Button data-testid="about-consult-btn" onClick={() => handleButtonClick('About - Schedule Consultation')}
                className="bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm px-8 py-5 rounded-md">
                <span className="flex items-center gap-2">Protect My Brand <ArrowRight className="h-4 w-4" /></span>
              </Button>
              
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1758518730178-6e237bc8b87d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTA2Njc3OXww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-3">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              We do not just file paperwork. We build protection strategies that align with your business goals and set you up for long-term success.
            </p>
          </div>

          {/* Slider - 3 slides on desktop, 1 slide on mobile with autoplay */}
          <div className="mx-12">
            <div className="overflow-hidden px-8" ref={whyChooseUsRef}>
              <div className="flex gap-8">
              {[
                { icon: ShieldCheck, title: 'Comprehensive Trademark Search', desc: 'Our detailed trademark search process helps identify potential conflicts before filing, reducing risks and helping businesses move forward with greater confidence.' },
                { icon: Users, title: 'Dedicated Trademark Specialists', desc: 'Receive personalized support throughout your trademark registration journey. From trademark search to trademark filing, your application receives focused attention every step of the way.' },
                { icon: ChartLineUp, title: 'Brand-Focused Protection', desc: 'We help businesses protect their brand names, logos, slogans, and intellectual property with strategies designed to support long term growth and brand recognition.' },
                { icon: Target, title: 'Trademark Monitoring Services', desc: 'Brand protection doesn\'t stop after registration. Our trademark monitoring solutions help businesses stay informed about potential conflicts and unauthorized use of their trademarks.' },
                { icon: Briefcase, title: 'Transparent Trademark Services', desc: 'Simple pricing and straightforward trademark filing services with no hidden costs. Know exactly what to expect before starting your trademark application.' },
                { icon: Scales, title: 'End-to-End Brand Protection', desc: 'From trademark registration and trademark renewal to ongoing brand protection support, we provide solutions designed to help secure and strengthen your intellectual property portfolio.' }
              ].map((item, i) => (
                <div key={i} data-testid={`about-card-${i}`} className="flex-[0_0_100%] sm:flex-[0_0_calc(50%-16px)] lg:flex-[0_0_calc(33.333%-21px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 hover:bg-[#044B3B] transition-all duration-300 group">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-white" weight="regular" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-white group-hover:text-white">{item.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed group-hover:text-white/90">{item.desc}</p>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Our Expertise</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-6">
                Attorneys Who Understand Your Business
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Our legal team brings decades of combined experience in intellectual property law. Every attorney on our team is USPTO-registered, which means they are authorized to practice directly before the United States Patent and Trademark Office.
              </p>

              <div className="space-y-5 mb-8">
                {[
                  { title: 'Trademark Strategy & Registration', desc: 'We handle 500+ trademark filings annually across every industry, from tech startups to luxury brands to Amazon sellers.' },
                  { title: 'IP Portfolio Architecture', desc: 'We help businesses build comprehensive IP portfolios that protect current operations and support future expansion into new markets.' },
                  { title: 'Enforcement & Defense', desc: 'When competitors threaten your brand, we respond with strategic enforcement actions including monitoring, cease-and-desist, and litigation support.' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" weight="bold" />
                    </div>
                    <div>
                      <h4 className="text-base font-heading font-semibold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button data-testid="about-consult-btn" onClick={() => handleButtonClick('About - Schedule Consultation')}
                className="bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm px-8 py-5 rounded-md">
                <span className="flex items-center gap-2">Schedule a Consultation <ArrowRight className="h-4 w-4" /></span>
              </Button>
            </div>

            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="/assets/img/whychooseus.jpg"
                  alt="Attorney reviewing documents" 
                  className="w-full h-[520px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">How We Work</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">
              Your Protection Journey
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              From your first consultation to ongoing brand monitoring, we make the entire process transparent and efficient.
            </p>
          </div>

          {/* Grid for desktop, slider for mobile */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-xs:hidden">
            {[
              { step: '01', title: 'Free Consultation', desc: 'Submit your brand details for a complimentary trademark evaluation. Our IP specialists assess registrability and identify potential conflicts within 24 hours.', img: 'https://images.unsplash.com/photo-1758518729929-8210d3b0839e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTA2Njc3OXww&ixlib=rb-4.1.0&q=85' },
              { step: '02', title: 'Comprehensive Search', desc: 'We search federal databases, state records, common law sources, and domain registries to identify any conflicts that could block your trademark.', img: 'https://images.unsplash.com/photo-1750277120336-ca98ec2e2f90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85' },
              { step: '03', title: 'Attorney Filing', desc: 'Your assigned attorney prepares and files a complete, optimized trademark application with the USPTO within 24-48 hours.', img: 'https://images.unsplash.com/photo-1769038947088-62455c949efc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85' },
              { step: '04', title: 'Ongoing Protection', desc: 'After registration, we provide continuous monitoring, handle renewals, and defend your brand against infringement threats.', img: 'https://images.pexels.com/photos/7433859/pexels-photo-7433859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' }
            ].map((item, i) => (
              <div key={i} className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-44 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 bg-[#044B3B] text-white text-xs font-bold px-3 py-1.5 rounded-md font-heading">
                    Step {item.step}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-base font-heading font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Slider for mobile (576px and below) */}
          <div className="overflow-hidden xs:hidden" ref={howWeWorkRef}>
            <div className="flex gap-4">
              {[
                { step: '01', title: 'Free Consultation', desc: 'Submit your brand details for a complimentary trademark evaluation. Our IP specialists assess registrability and identify potential conflicts within 24 hours.', img: 'https://images.unsplash.com/photo-1758518729929-8210d3b0839e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTA2Njc3OXww&ixlib=rb-4.1.0&q=85' },
                { step: '02', title: 'Comprehensive Search', desc: 'We search federal databases, state records, common law sources, and domain registries to identify any conflicts that could block your trademark.', img: 'https://images.unsplash.com/photo-1750277120336-ca98ec2e2f90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85' },
                { step: '03', title: 'Attorney Filing', desc: 'Your assigned attorney prepares and files a complete, optimized trademark application with the USPTO within 24-48 hours.', img: 'https://images.unsplash.com/photo-1769038947088-62455c949efc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85' },
                { step: '04', title: 'Ongoing Protection', desc: 'After registration, we provide continuous monitoring, handle renewals, and defend your brand against infringement threats.', img: 'https://images.pexels.com/photos/7433859/pexels-photo-7433859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' }
              ].map((item, i) => (
                <div key={i} className="flex-[0_0_85%] group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative h-44 overflow-hidden">
                    <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 bg-[#044B3B] text-white text-xs font-bold px-3 py-1.5 rounded-md font-heading">
                      Step {item.step}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base font-heading font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#044B3B]/90"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-6">
            Ready to protect your brand?
          </h2>
          <p className="text-base text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let our expert team secure your intellectual property rights with proven, results-driven legal services. Start with a free, no-obligation consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button data-testid="about-cta-register" onClick={() => handleButtonClick('About CTA - Register')}
              className="bg-white text-[#044B3B] hover:bg-slate-100 font-semibold text-sm px-8 py-5 rounded-md">
              <span className="flex items-center gap-2">Start Registration <ArrowRight className="h-4 w-4" /></span>
            </Button>
            <Button data-testid="about-cta-chat" onClick={() => window.cd360Api && window.cd360Api.liveChat.showWebWidget()}
              className="bg-transparent border border-white/30 text-white hover:bg-white/10 font-semibold text-sm px-8 py-5 rounded-md">
              Speak with an Attorney
            </Button>
          </div>
        </div>
      </section>

      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default About;
